import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpResponse,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { AuthService } from '../../services/auth/auth.service';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { APP } from '../../config/app';
import { NotificationTypes } from 'src/app/models/enums';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { MessageService, NotificationService, StorageService, SuggestionService } from 'src/app/services';
import { SILENT_ERROR_STATUSES } from 'src/app/services/http/base.http.service';
import { images } from 'src/images';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

const LOGIN_PATHS = ['login', '2FA/verify', 'refresh-token'];

@Injectable()
export class ResponseInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);

  constructor(
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private storageService: StorageService,
    private messageService: MessageService,
    private suggestionService: SuggestionService,
    private injector: Injector,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(request).pipe(
      map((event) => {
        if (event instanceof HttpResponse) {
          this.shouldUpdateState(event);
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.isLoginRequest(request.url)) {
          return throwError(() => error);
        }
        const token = this.authService.getToken();
        const refreshToken = this.authService.getRefreshToken();
        if (error.status === 401) {
          if (refreshToken) {
            return this.handle401Error(request, next);
          }
          this.logout();
          return throwError(() => new Error('Session expired'));
        }
        if (!token && !refreshToken) {
          this.logout();
          return throwError(() => new Error('No credentials'));
        }
        return this.handleError(error, request.context.get(SILENT_ERROR_STATUSES).includes(error.status));
      }),
    );
  }

  private isLoginRequest(url: string): boolean {
    return LOGIN_PATHS.some((path) => url.startsWith(`${environment.authUrl}/${path}`));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((value) => value !== null),
        take(1),
        switchMap((refreshed) => {
          if (!refreshed) {
            return throwError(() => new Error('Session expired'));
          }
          return next.handle(this.cloneWithToken(request));
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return from(this.refreshSession()).pipe(
      switchMap((refreshed) => {
        this.isRefreshing = false;
        if (!refreshed) {
          this.refreshTokenSubject.next(false);
          this.logout();
          return throwError(() => new Error('Session expired'));
        }
        this.refreshTokenSubject.next(true);
        return next.handle(this.cloneWithToken(request));
      }),
      catchError((err) => {
        if (this.isRefreshing) {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);
          if (err instanceof HttpErrorResponse && err.status === 401) {
            this.logout();
          }
        }
        return throwError(() => err);
      })
    );
  }

  private async refreshSession(): Promise<boolean> {
    const userInfoId = await this.getUserInfoId();
    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken || !userInfoId) {
      return false;
    }
    try {
      const response = await this.authService.refreshToken(userInfoId, refreshToken) as any;
      const expireDate = response?.body?.expireDate ?? response?.body?.expire_date;
      if (!expireDate) {
        return false;
      }
      const newRefreshToken = response.headers.get('X-BOD-REFRESH-TOKEN');
      if (newRefreshToken) {
        this.authService.setCookie(APP.LOCAL_STORAGE_REFRESH_TOKEN, newRefreshToken, 30);
      }
      this.authService.setCookie(APP.LOCAL_STORAGE_USER_ID, userInfoId.toString(), 30);
      const token = this.authService.getToken();
      if (token) {
        this.authService.setCookie(APP.LOCAL_STORAGE_TOKEN_KEY_NAME, token, undefined, false);
      }
      return true;
    } catch (err) {
      if (this.sessionRefreshedByAnotherTab(refreshToken)) {
        return true;
      }
      if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
        return false;
      }
      throw err;
    }
  }

  private async getUserInfoId(): Promise<string | null> {
    let userInfoId = null;
    try {
      const userInfoStr = await this.storageService.getUserInfo();
      userInfoId = JSON.parse(userInfoStr || 'null')?.id;
    } catch {
      userInfoId = null;
    }
    return userInfoId || this.authService.getUserId();
  }

  private sessionRefreshedByAnotherTab(previousRefreshToken: string): boolean {
    const currentRefreshToken = this.authService.getRefreshToken();
    return !!currentRefreshToken && currentRefreshToken !== previousRefreshToken && this.authService.isAuthenticated();
  }

  private cloneWithToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.authService.getToken();
    return token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request;
  }

  private logout() {
    this.authService.clearStorage();
    this.storageService.clearStorage();
    this.messageService.stopPolling();
    this.suggestionService.stopPolling();
    this.router.navigate(['/'], { replaceUrl: true });
  }

  // Requests with responseType 'text' receive the backend error JSON unparsed
  private parseErrorBody(errorBody: any): any {
    if (typeof errorBody !== 'string') return errorBody;
    try {
      return JSON.parse(errorBody);
    } catch {
      return errorBody;
    }
  }

  private handleError(error: HttpErrorResponse, silent = false): Observable<never> {
    this.shouldUpdateState(error);
    const errorBody = this.parseErrorBody(error.error);
    const payload = errorBody ?? error;
    if (silent) return throwError(() => error);
    this.notificationService.image = images.error;
    if (errorBody?.error_code === ErrorsCode.INVALID_REQUEST) {
      this.setInvalidRequestNotification(errorBody);
    } else {
      let errorTitle;
      let errorMessage;
      if (errorBody && errorBody.error_code) {
        errorTitle = `${errorBody.error_code}_ERROR.title`;
        errorMessage = `${errorBody.error_code}_ERROR.text`;
      } else {
        errorTitle = 'errorOccurred';
        errorMessage = error.status;
      }
      this.notificationService.title = errorTitle!;
      this.notificationService.message = errorMessage.toString();
      this.notificationService.translate = true;
    }
    this.notificationService.show(NotificationTypes.DANGER);
    return throwError(() => payload);
  }

  private setInvalidRequestNotification(error: any) {
    const literalService = this.injector.get(LiteralService);
    this.notificationService.title = literalService.get(`${error.error_code}_ERROR.title`, true);
    this.notificationService.message = literalService.get(`${error.error_code}_ERROR.text`, true, { fieldName: error.entity_info });
    this.notificationService.translate = false;
  }

  private shouldUpdateState(event: any) {
    let authHeader = undefined;
    if (event.headers instanceof HttpHeaders) {
      authHeader = event.headers.get(APP.HEADER_RESPONSE_TOKEN_AUTH_KEY);
    }

    if (authHeader) {
      const tokenType = authHeader.split(' ')[0];
      const token = authHeader.split(' ')[1];
      this.authService.setToken(token).setTokenType(tokenType);
      this.authService.setRole();
    }

    return this;
  }
}
